import React, { useState } from "react";
import clsx from "clsx";
import Select, {
  components,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  MultiValueRemoveProps,
} from "react-select";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <KeyboardArrowDownOutlinedIcon />
    </components.DropdownIndicator>
  );
};

const ClearIndicator = (props: ClearIndicatorProps) => {
  return (
    <components.ClearIndicator {...props}>
      <CloseIcon />
    </components.ClearIndicator>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <CloseIcon />
    </components.MultiValueRemove>
  );
};

const controlStyles = {
  base: "border min-h-[12px] rounded-[8px] bg-white hover:cursor-pointer ",
  focus: "border-blue-600  ring-1 ring-primary-600   ",
  nonFocus: "border-gray-200 ",
};

const placeholderStyles = "text-gray-700   pl-1 py-0.5";
const selectInputStyles = "pl-1 py-0.5    w-36";
const valueContainerStyles = "p-1 gap-1    ";
const singleValueStyles = "leading-7 ml-1 ";
const multiValueStyles =
  "bg-gray-100 rounded items-center py-0.5 pl-2 pr-1 gap-1.5   ";
const multiValueLabelStyles = "leading-6 py-0.5 ";
const multiValueRemoveStyles =
  "border border-gray-200  bg-white hover:bg-red-50 hover:text-red-800 text-gray-500   hover:border-red-300 rounded-md";
const indicatorsContainerStyles = "  ";
const clearIndicatorStyles =
  "text-gray-500 p-1 rounded-[8px] hover:bg-red-50 hover:text-red-800 ";
const indicatorSeparatorStyles = "bg-gray-300 ";
const dropdownIndicatorStyles =
  "p-1 hover:bg-gray-100  text-gray-500   rounded-[8px] hover:text-black";
const menuStyles =
  "p-1 mt-2 border border-gray-200  bg-white     rounded-[8px]";
const groupHeadingStyles = "ml-3 mt-2 mb-1 text-gray-500  ";
const optionStyles = {
  base: "hover:cursor-pointer px-3 py-1  rounded",
  focus:
    "bg-blue-200  active:bg-gray-200  ",
  selected:
    "after:content-['✔'] after:ml-2 after:text-blue-500  text-gray-900  ",
};
const noOptionsMessageStyles =
  "text-gray-500 p-2 bg-gray-50  border border-dashed border-gray-200 rounded-[8px]";

const ReactSelect = ({
  value,
  options,
  onChange,
  onInputChange,
  noOptionsMessage,
  placeholder,
  ...props
}) => (
  <Select
    closeMenuOnSelect={false}
    hideSelectedOptions={false}
    unstyled
    styles={{
      input: (base) => ({
        ...base,
        "input:focus": {
          boxShadow: "none",
        },
      }),
      multiValueLabel: (base) => ({
        ...base,
        whiteSpace: "normal",
        overflow: "visible",
      }),
      control: (base) => ({
        ...base,
        transition: "none",
      }),
    }}
    components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
    classNames={{
      control: ({ isFocused }) =>
        clsx(
          isFocused ? controlStyles.focus : controlStyles.nonFocus,
          controlStyles.base
        ),
      placeholder: () => placeholderStyles,
      input: () => selectInputStyles,
      valueContainer: () => valueContainerStyles,
      singleValue: () => singleValueStyles,
      multiValue: () => multiValueStyles,
      multiValueLabel: () => multiValueLabelStyles,
      multiValueRemove: () => multiValueRemoveStyles,
      indicatorsContainer: () => indicatorsContainerStyles,
      clearIndicator: () => clearIndicatorStyles,
      indicatorSeparator: () => indicatorSeparatorStyles,
      dropdownIndicator: () => dropdownIndicatorStyles,
      menu: () => menuStyles,
      groupHeading: () => groupHeadingStyles,
      option: ({ isFocused, isSelected }) =>
        clsx(
          isFocused && optionStyles.focus,
          isSelected && optionStyles.selected,
          optionStyles.base
        ),
      noOptionsMessage: () => noOptionsMessageStyles,
    }}
    value={value}
    options={options}
    onChange={onChange}
    onInputChange={onInputChange}
    placeholder={placeholder}
    noOptionsMessage={() => noOptionsMessage}
    {...props}
  />
);

function SelectModal({
  value,
  options,
  onChange,
  onInputChange,
  noOptionsMessage,
  placeholder,
}) {
  const sortedOptions = Array.isArray(options)
    ? options.slice().sort((a, b) => a.label?.localeCompare(b.label))
    : [];

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  return (
    <ReactSelect
      value={value}
      options={sortedOptions}
      onChange={(selectedOption) => {
        onChange(selectedOption);
        handleMenuClose();
      }}
      noOptionsMessage={noOptionsMessage}
      placeholder={placeholder}
      onInputChange={onInputChange}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      menuIsOpen={isMenuOpen}
    />
  );
}

export default SelectModal;
